import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { CENTER_ALIGNED, RIGHT_ALIGNED } from '../../defaults';
import type { CellContext, ResolvedColumn } from '../../domain';
import { renderEditableCell } from '../editing/CellEditing';

export interface RowDropProps {
    dragging: boolean;
    dropEdge?: 'before' | 'after';
    onDragOver: () => void;
    onDrop: () => void;
}

export interface RowProps<T> {
    row: T;
    rowIndex: number;
    columns: ResolvedColumn<T>[];
    api: CellContext<T>['api'];
    selected?: boolean;
    onClick?: (row: T) => void;
    leading?: ReactNode;
    trailing?: ReactNode;
    drop?: RowDropProps;
    dragHandle?: ReactNode;
}

const INTERACTIVE_SELECTOR = [
    'button',
    'a',
    'input',
    'select',
    'textarea',
    '[role="menuitem"]',
    '[role="button"]',
    '[role="listbox"]',
    '[role="option"]',
    '.ics-table__cell-editable',
    '.ics-table__cell--editing',
].join(', ');

export function columnStyle<T>(col: ResolvedColumn<T>): CSSProperties {
    if (col.width !== undefined) {
        return { width: col.width, minWidth: col.width, maxWidth: col.width };
    }
    return { minWidth: col.minWidth };
}

function cellContent<T>(
    col: ResolvedColumn<T>,
    ctx: CellContext<T>,
): ReactNode {
    if (col.render) {
        return col.render(ctx);
    }
    const { value } = ctx;
    if (col.labels && typeof value === 'string' && value in col.labels) {
        return col.labels[value];
    }
    if (value === null || value === undefined) {
        return '';
    }
    return String(value);
}

const FIELD_SELECTOR =
    'input, select, textarea, [role="listbox"], [role="combobox"]';

function originatesFromControl(
    event: MouseEvent<HTMLTableRowElement>,
): boolean {
    const target = event.target as HTMLElement;
    if (target.closest(INTERACTIVE_SELECTOR)) {
        return true;
    }
    return Boolean(target.closest('td')?.querySelector(FIELD_SELECTOR));
}

function resolveAlign<T>(col: ResolvedColumn<T>): 'left' | 'center' | 'right' {
    if (col.align) return col.align;
    if (RIGHT_ALIGNED.includes(col.type ?? '')) return 'right';
    if (CENTER_ALIGNED.includes(col.type ?? '')) return 'center';
    return 'left';
}

function cellClassName<T>(col: ResolvedColumn<T>, editing: boolean): string {
    const align = resolveAlign(col);
    const alignPart = align === 'left' ? '' : ` ics-table__cell--${align}`;
    const editingPart = editing ? ' ics-table__cell--editing' : '';
    return `ics-table__cell${alignPart}${editingPart}`;
}

function rowClassName(
    selected: boolean | undefined,
    drop: RowDropProps | undefined,
): string {
    return [
        'ics-table__row',
        selected ? 'ics-table__row--selected' : '',
        drop?.dragging ? 'ics-table__row--dragging' : '',
        drop?.dropEdge ? `ics-table__row--drop-${drop.dropEdge}` : '',
    ]
        .filter(Boolean)
        .join(' ');
}

export function Row<T>(props: RowProps<T>): JSX.Element {
    const {
        row,
        rowIndex,
        columns,
        api,
        selected,
        onClick,
        leading,
        trailing,
        drop,
        dragHandle,
    } = props;

    const handleClick = (event: MouseEvent<HTMLTableRowElement>): void => {
        if (!onClick || originatesFromControl(event)) {
            return;
        }
        onClick(row);
    };

    return (
        <tr
            className={rowClassName(selected, drop)}
            onClick={handleClick}
            aria-selected={selected}
            onDragOver={
                drop
                    ? (e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          drop.onDragOver();
                      }
                    : undefined
            }
            onDrop={
                drop
                    ? () => {
                          drop.onDrop();
                      }
                    : undefined
            }
        >
            {leading}
            {columns.map((col, colIndex) => {
                const value = col.getValue(row);
                const ctx: CellContext<T> = {
                    row,
                    value,
                    rowIndex,
                    column: col,
                    api,
                };
                const content = cellContent(col, ctx);
                const { editing, node } = renderEditableCell(col, ctx, content);
                const handle = colIndex === 0 ? dragHandle : null;

                return (
                    <td
                        key={col.id}
                        style={columnStyle(col)}
                        className={cellClassName(col, editing)}
                    >
                        {handle ? (
                            <div className="ics-table__cell-drag-wrap">
                                {handle}
                                <div className="ics-table__cell-drag-content">
                                    {node}
                                </div>
                            </div>
                        ) : (
                            node
                        )}
                    </td>
                );
            })}
            {trailing}
        </tr>
    );
}

export interface ExpandedRowProps {
    colSpan: number;
    children: ReactNode;
}

export function ExpandedRow(props: ExpandedRowProps): JSX.Element {
    const { colSpan, children } = props;

    return (
        <tr className="ics-table__expanded-row">
            <td colSpan={colSpan}>{children}</td>
        </tr>
    );
}
